import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import AtlasChatbot, {
  AtlasChatbotProps,
  openBlankTab,
  showDocumentInTab,
} from "@amzn/atlas-chat-react-library";
import "@amzn/atlas-chat-react-library/dist/style.css";
import { agentsForTeam } from "pages/chatbot/agents";
import { dedupeMessagesById } from "pages/chatbot/messages";
import { useMinervaSettleRefetch } from "hooks/useMinervaSettleRefetch";
import {
  useGetChatAuthQuery,
  useLazyGetConversationDetailsQuery,
  useLazyGetCitationQuery,
  useSaveFeedbackMutation,
} from "services/atlas-chat";
import { useUserNameSelector } from "pages/team_settings/hooks";
import { useFullNameSelector } from "hooks/useNameSelector";
import { useSettings } from "features/settings/hooks";
import { useSelectedTeam } from "features/team-settings/hooks";
import { useNotifications } from "features/notifications/hooks";
import { LOG } from "utils";
import { GaiaConstants } from "utils/gaia-constants";
import { Modal, SpaceBetween, Box, Button } from "@amzn/awsui-components-react";
import { buildConversationListRequests } from "utils/conversation-list-requests";
import ConversationGroup from "pages/conversational-history/ConversationGroup";
import styles from "./style.module.scss";

interface AnswersChatContentProps {
  onHeaderActions?: (actions: React.ReactNode) => void;
}

export interface AnswersChatContentHandle {
  newChat: () => void;
}

const AnswersChatContent = forwardRef<
  AnswersChatContentHandle,
  AnswersChatContentProps
>(({ onHeaderActions }, ref) => {
  const { t } = useTranslation();
  const { addNotification } = useNotifications();
  const { userName } = useUserNameSelector();
  const { fullName: userFullName } = useFullNameSelector();
  const { settings } = useSettings();
  const { isMinervaKbEnabled } = useSelectedTeam();
  const selectedTeamName =
    localStorage.getItem(GaiaConstants.SELECTED_TEAM_NAME) || "";

  // Without an agents list the sidebar sends no agentId and the service falls
  // back to its configured default; pass the same list the full page reads so
  // both views resolve the same agent.
  const agents = useMemo(
    () => agentsForTeam(selectedTeamName, isMinervaKbEnabled),
    [selectedTeamName, isMinervaKbEnabled],
  );

  const [conversation, setConversation] = useState<
    AtlasChatbotProps.Conversation | undefined
  >();
  const [isConversationLoading, setIsConversationLoading] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [chatKey, setChatKey] = useState(0);

  const {
    data: chatAuthToken,
    isError: isChatAuthError,
    error: chatAuthError,
    refetch: refetchChatAuth,
  } = useGetChatAuthQuery(
    { appId: selectedTeamName },
    { skip: !selectedTeamName, refetchOnMountOrArgChange: true },
  );

  const [triggerGetConversationDetails] = useLazyGetConversationDetailsQuery();
  const [saveFeedbackRequest] = useSaveFeedbackMutation();
  const [triggerGetCitation] = useLazyGetCitationQuery();

  const conversationListRequests = useMemo(
    () =>
      userName && selectedTeamName
        ? buildConversationListRequests({
            user: userName,
            appId: selectedTeamName,
          })
        : [],
    [userName, selectedTeamName],
  );

  const groupTitles = useMemo(
    () => [
      t("pages.gaia_ai.conversational_history.today"),
      t("pages.gaia_ai.conversational_history.yesterday"),
      t("pages.gaia_ai.conversational_history.lastSevenDays"),
      t("pages.gaia_ai.conversational_history.lastThirtyDays"),
    ],
    [t],
  );

  const handleConversationSelect = useCallback(
    async (selectedConversationId: string) => {
      setIsConversationLoading(true);
      setConversationId(selectedConversationId);
      try {
        const data = await triggerGetConversationDetails({
          appId: selectedTeamName,
          conversationId: selectedConversationId,
        }).unwrap();

        if (data) {
          const conversationDetails: AtlasChatbotProps.Conversation = {
            conversationId: selectedConversationId,
            messages: [...(data.messages || [])].sort(
              (a, b) =>
                new Date(a.createdAt!).getTime() -
                new Date(b.createdAt!).getTime(),
            ),
            ...(data.paginationToken && {
              paginationToken: data.paginationToken,
            }),
          };
          setConversation(conversationDetails);
          setHasMoreMessages(!!data.paginationToken);
        }
      } catch (e) {
        LOG.error(e);
        addNotification({
          type: "error",
          header: "Failed to load conversation",
          dismissible: true,
          timeout: 5000,
        });
      } finally {
        setIsConversationLoading(false);
      }
    },
    [selectedTeamName, triggerGetConversationDetails, addNotification],
  );

  const handleLoadMoreMessages = useCallback(async () => {
    if (!conversationId || !conversation?.paginationToken) return;
    try {
      const data = await triggerGetConversationDetails({
        appId: selectedTeamName,
        conversationId,
        paginationToken: conversation.paginationToken,
      }).unwrap();

      if (data) {
        const updatedConversation: AtlasChatbotProps.Conversation = {
          conversationId,
          messages: [
            ...(conversation.messages || []),
            ...(data.messages || []),
          ].sort(
            (a, b) =>
              new Date(a.createdAt!).getTime() -
              new Date(b.createdAt!).getTime(),
          ),
          ...(data.paginationToken && {
            paginationToken: data.paginationToken,
          }),
        };
        setConversation(updatedConversation);
        setHasMoreMessages(!!data.paginationToken);
      }
    } catch (e) {
      LOG.error(e);
    }
  }, [
    conversationId,
    conversation,
    selectedTeamName,
    triggerGetConversationDetails,
  ]);

  const handleNewChat = useCallback(() => {
    setConversation(undefined);
    setConversationId(undefined);
    setHasMoreMessages(false);
    setChatKey((k) => k + 1);
  }, []);

  useImperativeHandle(ref, () => ({ newChat: handleNewChat }), [handleNewChat]);

  useEffect(() => {
    onHeaderActions?.(
      <Button
        variant="icon"
        iconName="history"
        onClick={() => setIsHistoryOpen(true)}
        ariaLabel="Conversation history"
      />,
    );
  }, [onHeaderActions]);

  const handleError = useCallback((error: any) => {
    LOG.error(error);
  }, []);

  // Reconcile the just-streamed turn with the persisted server copy, which is
  // the only copy that carries the finalized answer with inline `<cite>`
  // markers and their citations. Without it the sidebar keeps the raw streamed
  // message and renders no inline citations, unlike the full page. Mirrors
  // Chatbot.tsx so both surfaces reconcile identically.
  const refetchConversationDetails = useCallback(async () => {
    if (!conversationId || !selectedTeamName) {
      return;
    }
    try {
      const data = await triggerGetConversationDetails({
        appId: selectedTeamName,
        conversationId,
      }).unwrap();

      // Only reconcile when the persisted server copy actually has messages.
      // The RTK query resolves to a truthy object even when `messages` is
      // empty/omitted; for a brand-new conversation `prev` is still undefined
      // while the streamed turn lives inside the chat library, so merging an
      // empty server copy within the settle window would overwrite state with
      // `messages: []` and blank the just-streamed answer.
      if (data?.messages?.length) {
        // The settle reconcile deliberately fetches only page 1 to pull the
        // finalized turn (its inline `<cite>` citations); it must NOT
        // re-paginate. Preserve the existing pagination cursor and leave
        // `hasMoreMessages` untouched — otherwise a settle after the user has
        // paged back through history would resurrect the "Load more" button and
        // reset the token to page 1's, re-fetching an already-loaded page. The
        // initial load (handleConversationSelect) and manual "load more"
        // (handleLoadMoreMessages) still own pagination state.
        setConversation((prev) => ({
          conversationId,
          messages: dedupeMessagesById([
            ...(prev?.messages || []),
            ...(data.messages || []),
          ]).sort(
            (a, b) =>
              new Date(a.createdAt!).getTime() -
              new Date(b.createdAt!).getTime(),
          ),
          ...(prev?.paginationToken && {
            paginationToken: prev.paginationToken,
          }),
        }));
      }
    } catch (e) {
      LOG.error(e);
    }
  }, [conversationId, selectedTeamName, triggerGetConversationDetails]);

  // settle-window refetch, gated behind the Minerva flag so the Atlas
  // (flag-off) flow arms no timer and flag-off is a true rollback.
  const onAgentSettleRefetch = useMinervaSettleRefetch(
    refetchConversationDetails,
    { enabled: isMinervaKbEnabled },
  );

  const handleOnMessageReceived = useCallback(
    (message: AtlasChatbotProps.ConversationMessage) => {
      if (!conversationId && message.conversationId) {
        setConversationId(message.conversationId);
      }
      if (message.senderType === "SYSTEM") {
        addNotification({
          type: "error",
          header: `${message.content}`,
          dismissible: true,
          timeout: 5000,
        });
      }

      if (message.senderType === "AGENT") {
        // Reset the settle window on every streamed AGENT chunk so exactly one
        // refetch fires after the turn settles, reconciling with the persisted
        // server copy that carries the inline citations. No-op when Minerva is
        // off.
        onAgentSettleRefetch();
      }
    },
    [addNotification, conversationId, onAgentSettleRefetch],
  );

  const handleSaveFeedback = useCallback(
    async (
      message: AtlasChatbotProps.ConversationMessage,
      liked: boolean,
      feedback?: string,
    ): Promise<any> => {
      try {
        await saveFeedbackRequest({
          appId: selectedTeamName || "",
          conversationId: message.conversationId,
          messageId: message.messageId,
          liked,
          feedback,
        }).unwrap();
      } catch (_e) {
        addNotification({
          type: "error",
          header: t("pages.gaia_ai.chatbot.feedbackError"),
          dismissible: true,
          timeout: 5000,
        });
      }
    },
    [addNotification, saveFeedbackRequest, selectedTeamName, t],
  );

  const handleCitationClick = useCallback(
    async (citation: AtlasChatbotProps.Citation) => {
      const { messageId, citationId, conversationId: citConvId } = citation;

      if (!messageId || !citationId || !citConvId) {
        return;
      }

      // Shared so a falsy url / blocked popup surfaces the same feedback as a
      // thrown error, rather than silently closing the tab with no explanation.
      // Mirrors Chatbot.tsx's citation-open failure handling and reuses its key.
      const notifyCitationError = () =>
        addNotification({
          type: "error",
          header: t("pages.gaia_ai.chatbot.citationError", {
            defaultValue: "Unable to open the cited document",
          }),
          dismissible: true,
          timeout: 5000,
        });

      // Open a blank window synchronously while still in the user gesture call
      // stack, then set its location once the URL resolves. This avoids popup
      // blockers in strict browsers (e.g. Safari) while still preventing
      // reverse tabnapping via opener = null.
      const newWindow = openBlankTab();
      try {
        const url = await triggerGetCitation({
          appId: selectedTeamName,
          conversationId: citConvId,
          messageId,
          citationId,
        }).unwrap();

        if (url && newWindow) {
          // the cited page comes from the citation itself, so the tab lands on it
          await showDocumentInTab(newWindow, {
            url,
            page: citation.page,
            filename: citation.title,
          });
        } else {
          // Falsy url, or the browser blocked the popup (newWindow is null):
          // the user clicked and would otherwise get nothing, so tell them why.
          newWindow?.close();
          notifyCitationError();
        }
      } catch {
        // Scrubbed on purpose: never log the caught error — a rejected fetch
        // or unwrap can carry the presigned url or response body (PII rule).
        // Log a generic marker and surface a generic toast instead.
        newWindow?.close();
        LOG.error("Failed to open cited document");
        notifyCitationError();
      }
    },
    [addNotification, selectedTeamName, t, triggerGetCitation],
  );

  const handleReconnectStop = useCallback(() => {
    if (selectedTeamName && chatAuthToken !== undefined) {
      refetchChatAuth();
    }
  }, [chatAuthToken, refetchChatAuth, selectedTeamName]);

  useEffect(() => {
    if (isChatAuthError) {
      LOG.error(chatAuthError);
    }
  }, [isChatAuthError, chatAuthError]);

  const handleConversationClick = useCallback(
    (id: string) => {
      handleConversationSelect(id);
      setIsHistoryOpen(false);
    },
    [handleConversationSelect],
  );

  return (
    <div className={`${styles.container} ${styles.sidebarVariant}`}>
      <AtlasChatbot
        key={chatKey}
        variant="sidebar"
        protocol="ag-ui"
        appId={selectedTeamName}
        chatAuthToken={chatAuthToken}
        socketUrl={settings.endpoints.atlasWebSocketUrl}
        conversation={conversation}
        isConversationLoading={isConversationLoading}
        hasMoreMessages={hasMoreMessages}
        onLoadMoreMessages={handleLoadMoreMessages}
        onSaveFeedback={handleSaveFeedback}
        onError={handleError}
        userAlias={userName}
        userFullName={userFullName}
        onMessageReceived={handleOnMessageReceived}
        onReconnectStop={handleReconnectStop}
        onCitationClick={handleCitationClick}
        agents={agents}
        i18nStrings={{
          greeting: t("pages.gaia_ai.chatbot.greeting", {
            userAlias: userName,
          }),
          helpMessage: t("pages.gaia_ai.chatbot.helpMessage"),
          inputPlaceholder: t("pages.gaia_ai.chatbot.inputPlaceholder"),
          connectedMessage: t("pages.gaia_ai.chatbot.connectedMessage"),
          notConnectedMessage: t("pages.gaia_ai.chatbot.notConnectedMessage"),
          disclaimerText: t("pages.gaia_ai.chatbot.disclaimerText"),
          termsAndConditions: t("pages.gaia_ai.chatbot.termsAndConditions"),
          textCopiedMessage: t("pages.gaia_ai.chatbot.textCopiedMessage"),
        }}
      />
      <Modal
        visible={isHistoryOpen}
        onDismiss={() => setIsHistoryOpen(false)}
        header="Conversation History"
        size="large"
      >
        {conversationListRequests.length === 0 ? (
          <Box textAlign="center" padding="l">
            <Box variant="p">No conversations yet.</Box>
          </Box>
        ) : (
          <SpaceBetween size="l">
            {conversationListRequests.map((request, index) => (
              <ConversationGroup
                key={`conversation-group-${index}`}
                title={groupTitles[index]}
                conversationListRequest={request}
                onDataUpdated={() => setForceRefresh((prev) => prev + 1)}
                forceRefresh={forceRefresh}
                onConversationClick={handleConversationClick}
              />
            ))}
          </SpaceBetween>
        )}
      </Modal>
    </div>
  );
});

AnswersChatContent.displayName = "AnswersChatContent";

export { AnswersChatContent };
export type { AnswersChatContentProps };
export default AnswersChatContent;
